import { useEffect, useState } from "react";
import { Instagram, Linkedin, Download, Copy, X, ExternalLink, Check, Heart } from "lucide-react";
import api from "@/lib/api";

const DELAY_MS = 6000;
export const SITE = "https://caesarsghost.ca";
const IMAGE_PATH = "/share-image.png";
export const CAPTIONS = {
  glimpse: "I just explored my human authority with Caesar's Ghost, and it gave me something worth thinking about: what really influences my decisions?\nWhere does your authority lie?\nDiscover yours at https://caesarsghost.ca\n#CaesarsGhost #HumanAuthority",
  portrait: "I've completed my Authority Portrait with Caesar's Ghost, and I'm exploring what shapes my decisions, especially under pressure.\nWhere does your authority lie?\nDiscover yours at https://caesarsghost.ca\n#CaesarsGhost #HumanAuthority",
};
const BIG = "w-full inline-flex items-center justify-center gap-2 px-3 sm:px-6 py-3 sm:py-3.5 rounded-full font-medium text-sm sm:text-base cg-scale-btn transition-colors";
const SMALL = "inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full text-sm border border-white/15 text-[#E8EAEE] hover:bg-white/5 transition-colors";

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); return true; } catch { /* fall through */ }
  const ta = document.createElement("textarea");
  ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { ok = false; }
  ta.remove();
  return ok;
};
// Phones/tablets only: desktop share sheets don't reach the LinkedIn or Instagram apps.
const isTouchDevice = () => window.matchMedia?.("(pointer: coarse)").matches && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent + (navigator.maxTouchPoints > 1 ? " iPad" : ""));

const useShareFile = () => {
  const [file, setFile] = useState(null);
  useEffect(() => {
    fetch(IMAGE_PATH).then((r) => r.blob()).then((b) => setFile(new File([b], "caesars-ghost.png", { type: "image/png" }))).catch(() => {});
  }, []);
  return file;
};

const GUIDES = {
  linkedin: ["LinkedIn's share window has opened with caesarsghost.ca and its Caesar's Ghost preview.", "Click “Copy Caption” below, then paste it into the post box.", "Click Post when you're ready."],
  linkedinManual: ["Tap “Copy Caption”.", "Tap “Open LinkedIn” — it opens with caesarsghost.ca and its Caesar's Ghost preview.", "Paste the caption into the post box and tap Post."],
  instagram: ["Click “Save Image” to save the Caesar's Ghost graphic.", "Click “Copy Caption”.", "Click “Open Instagram”, choose Create (+), add the saved image and paste the caption."],
};

export const ShareActions = ({ source, placement }) => {
  const caption = CAPTIONS[source] || CAPTIONS.portrait;
  const file = useShareFile();
  const [guide, setGuide] = useState(null);
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [thanked, setThanked] = useState(false);
  const track = (platform) => api.post("/glimpse/share-click", { platform, source, placement }).catch(() => {});

  const canNative = () => Boolean(isTouchDevice() && file && navigator.canShare?.({ files: [file] }));
  // Called straight from the tap (no awaits first) so Safari keeps the user gesture.
  const nativeShare = async (app) => {
    const pre = copyText(caption);
    setGuide(null);
    try {
      await navigator.share({ files: [file], text: caption });
      setThanked(true);
      const ok = await pre;
      setNote(app === "Instagram"
        ? `Choose Instagram. Instagram doesn't accept captions from other apps, so paste the caption${ok ? " (already copied)" : ""} into your post.`
        : `Choose LinkedIn. If the caption doesn't appear, paste it${ok ? " (already copied)" : ""}.`);
    } catch (e) {
      setNote("");
      if (e?.name !== "AbortError") { setGuide(app === "Instagram" ? "instagram" : "linkedinManual"); }
    }
  };

  const onLinkedIn = async () => {
    track("linkedin");
    if (canNative()) { nativeShare("LinkedIn"); return; }
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SITE)}`, "_blank", "noopener,noreferrer");
    setGuide("linkedin");
    setNote("");
    setCopied(await copyText(caption));
  };
  const onInstagram = () => {
    track("instagram");
    if (canNative()) { nativeShare("Instagram"); return; }
    setGuide("instagram");
    setNote("");
  };
  const onCopy = async () => {
    const ok = await copyText(caption);
    setCopied(ok);
    if (ok) { setThanked(true); }
    setNote(ok ? "" : "Select the caption above and copy it.");
  };

  return (
    <div data-testid={`share-actions-${placement}`}>
      <p className="text-xs sm:text-sm text-[#B9BDC7] whitespace-pre-line leading-relaxed select-text" data-testid={`share-caption-${placement}`}>{caption}</p>
      <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2.5">
        <button onClick={onLinkedIn} disabled={!file} data-testid={`share-linkedin-${placement}`}
          className={`${BIG} bg-[#0A66C2] text-white hover:bg-[#0958A8] disabled:opacity-60`}><Linkedin className="w-5 h-5 shrink-0" /> Share on LinkedIn</button>
        <button onClick={onInstagram} disabled={!file} data-testid={`share-instagram-${placement}`}
          className={`${BIG} bg-brass text-obsidian hover:bg-amber2 disabled:opacity-60`}><Instagram className="w-5 h-5 shrink-0" /> Share on Instagram</button>
      </div>
      {guide && (
        <div className="mt-4 rounded-xl border border-[rgba(217,162,95,0.3)] bg-white/[0.03] p-4" data-testid={`share-guide-${guide}-${placement}`}>
          <p className="text-sm font-medium text-[#F3F4F6] mb-2">{guide === "instagram" ? "Post on Instagram from this device" : "Finish your LinkedIn post"}</p>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-[#B9BDC7]">{GUIDES[guide].map((s) => <li key={s}>{s}</li>)}</ol>
          {guide === "linkedin" && copied && <p className="mt-2 text-xs text-[#9FC5A8]" data-testid={`share-auto-copied-${placement}`}>The caption has already been copied for you.</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={onCopy} className={SMALL} data-testid={`share-copy-${placement}`}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Caption Copied" : "Copy Caption"}</button>
            {guide === "instagram" && <a href={IMAGE_PATH} download="caesars-ghost.png" className={SMALL} data-testid={`share-save-${placement}`}><Download className="w-4 h-4" /> Save Image</a>}
            {guide === "instagram" && <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className={SMALL} data-testid={`share-open-instagram-${placement}`}><ExternalLink className="w-4 h-4" /> Open Instagram</a>}
            {guide !== "instagram" && <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SITE)}`} target="_blank" rel="noopener noreferrer" className={SMALL} data-testid={`share-reopen-linkedin-${placement}`}><ExternalLink className="w-4 h-4" /> {guide === "linkedin" ? "Open LinkedIn again" : "Open LinkedIn"}</a>}
          </div>
        </div>
      )}
      {thanked && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-[rgba(217,162,95,0.4)] bg-[rgba(217,162,95,0.08)] p-4 cg-fade-up" data-testid={`share-thanks-${placement}`}>
          <Heart className="w-5 h-5 text-[#D9A25F] shrink-0 mt-0.5" />
          <div>
            <p className="font-serif text-lg text-[#F3F4F6] leading-tight">Thank you for sharing</p>
            <p className="text-sm text-[#B9BDC7] mt-1">You've invited someone else to ask where their authority lies.</p>
          </div>
        </div>
      )}
      <p role="status" aria-live="polite" className="mt-3 text-sm text-[#B9BDC7] text-center min-h-[1.25rem]" data-testid={`share-status-${placement}`}>{note}</p>
    </div>
  );
};

export const ShareImage = ({ className = "" }) => (
  <div className={`mx-auto w-full aspect-square rounded-xl overflow-hidden border border-[rgba(217,162,95,0.3)] bg-[#0E1116] ${className}`}>
    <img src={IMAGE_PATH} alt="Caesar's Ghost logo with seven coloured bars and dots, caesarsghost.ca" className="w-full h-full" data-testid="share-image" />
  </div>
);

// Shows once per completed Portrait (per account, per browser), 6s after first opening it.
export const SharePopup = ({ userId, portraitKey, source }) => {
  const storageKey = `cg_share_prompt_${source}_${userId}_${portraitKey}`;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId || !portraitKey || localStorage.getItem(storageKey)) { return undefined; }
    const t = setTimeout(() => { localStorage.setItem(storageKey, new Date().toISOString()); setOpen(true); }, DELAY_MS);
    return () => clearTimeout(t);
  }, [userId, portraitKey, storageKey]);

  useEffect(() => {
    if (!open) { return undefined; }
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) { return null; }

  return (
    <div role="dialog" aria-modal="false" aria-labelledby="share-popup-title" data-testid="share-popup"
      className="fixed z-50 inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[420px] max-h-[88vh] overflow-y-auto cg-fade-up
                 bg-[#13171E] border border-[rgba(217,162,95,0.35)] rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 sm:p-6">
      <button onClick={() => setOpen(false)} aria-label="Close" data-testid="share-popup-close"
        className="absolute top-3 right-3 p-2 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5 transition-colors"><X className="w-5 h-5" /></button>
      <h2 id="share-popup-title" className="font-serif text-2xl text-[#F3F4F6] pr-8">Post to Social Media Now</h2>
      <ShareImage className="mt-3 sm:mt-4 max-w-[96px] sm:max-w-[240px]" />
      <div className="mt-3 sm:mt-4"><ShareActions source={source} placement="popup" /></div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-xs text-[#6B7280]">Nothing is posted for you. The image contains no personal results.</p>
        <button onClick={() => setOpen(false)} data-testid="share-popup-not-now"
          className="shrink-0 text-sm text-[#E8EAEE] border border-white/15 rounded-full px-4 py-2 hover:bg-white/5 transition-colors">Not Now</button>
      </div>
    </div>
  );
};
